<script setup lang="ts">
import { Badge } from '@/components/shad/ui/badge'
import useDarkMode from '@/composables/useDarkMode.store'
import useDeploymentWarning from '@/composables/useDeploymentWarning'
import useHighSeverityFindings from '@/composables/useHighSeverityFindings'
import {
  sidebarGroups,
  sidebarItemFor,
  type SidebarItem,
} from '@/router/sidebarNav'
import useAuthStore from '@/stores/auth'
import useLayoutStore from '@/stores/layout.store'
import { useUserRolesStore } from '@/stores/userRoles.store'
import type { Severity } from '@/types/constants/severities'
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TriangleAlertIcon,
} from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { computed, onMounted, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// constants
const severityBg: Record<Severity, string> = {
  CRITICAL: 'bg-critical hover:bg-critical',
  HIGH: 'bg-high hover:bg-high',
  MEDIUM: 'bg-medium hover:bg-medium',
  LOW: 'bg-low hover:bg-low',
  INFORMATIONAL: 'bg-informational hover:bg-informational',
}

// composables
const darkMode = useDarkMode()
const { sidebarCollapsed } = storeToRefs(useLayoutStore())
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const { badge: findingsBadge } = useHighSeverityFindings()
const userRolesStore = useUserRolesStore()
const deploymentWarning = useDeploymentWarning()

// state
/** Parent items whose children are shown. */
const expandedItems = reactive(new Set<string>())

// computed
const usersWarning = computed(
  () => userRolesStore.organizationsWithoutAdmin.length > 0,
)

/** Badge text sizes by label length, so "10+" fits the circle as well as "3". */
const findingsBadgeText = computed(() => {
  const length = findingsBadge.value?.label.length ?? 1
  return {
    expanded:
      length >= 3 ? 'text-[9px]' : length === 2 ? 'text-[10px]' : 'text-[11px]',
    collapsed: length >= 3 ? 'text-[8px]' : 'text-[9px]',
  }
})

/**
 * The item for the current route or the nearest route it's nested under, so
 * detail pages (`/findings/:id`, `/assets/:id`) keep their item active.
 */
const currentItem = computed(() =>
  route.matched.map((rt) => sidebarItemFor(rt.name)).findLast(Boolean),
)

/** Items flagged with a warning triangle rather than a count badge. */
const warnings = computed<Record<string, boolean>>(() => ({
  'platform:deployment': deploymentWarning.value,
  'platform:users': usersWarning.value,
}))

/** Entries the router guard would deny are hidden, as are emptied groups. */
const groups = computed(() => {
  const allowed = (routeName: string) => {
    const required = router.resolve({ name: routeName }).meta.requiredCapability
    return !required || auth.can(required)
  }
  return sidebarGroups
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => allowed(item.routeName))
        .map((item) => ({
          ...item,
          badge:
            item.routeName === 'findings' ? findingsBadge.value : undefined,
          badgeText: findingsBadgeText.value,
          warning: !!warnings.value[item.routeName],
          children: item.children?.filter((child) => allowed(child.routeName)),
        })),
    }))
    .filter((group) => group.items.length)
})

// methods
const toggleExpanded = (routeName: string) => {
  if (expandedItems.has(routeName)) {
    expandedItems.delete(routeName)
  } else {
    expandedItems.add(routeName)
  }
}

/**
 * Clicking a parent from its default child (the parent route's redirect)
 * doesn't navigate anywhere, so it toggles the children instead.
 */
const onParentClick = (item: SidebarItem) => {
  const target = router.resolve({ name: item.routeName })
  const option = target.matched.at(-1)?.redirect
  const redirect = typeof option === 'function' ? option(target, route) : option
  const defaultChild =
    typeof redirect === 'object' && 'name' in redirect
      ? redirect.name
      : undefined
  if (route.name === defaultChild) {
    toggleExpanded(item.routeName)
  } else {
    expandedItems.add(item.routeName)
  }
}

/** A parent is active on its children's routes too. */
const isItemActive = (item: SidebarItem): boolean =>
  currentItem.value?.routeName === item.routeName

/**
 * Both states share `h-10` so collapsing doesn't shift rows. Collapsed items
 * add a transparent right border to keep the icon centered; expanded items keep
 * a transparent left border to stay aligned with their children.
 */
const itemLinkClasses = (item: SidebarItem) => {
  const active = isItemActive(item)
  if (sidebarCollapsed.value) {
    return [
      'flex h-10 w-full items-center justify-center border-x-4 border-r-transparent text-sm text-default-font-color',
      active ? 'border-l-logo-font-color font-bold' : 'border-l-transparent',
    ]
  }
  return [
    'flex h-10 flex-1 items-center gap-x-4 border-l-4 border-transparent px-4 text-sm text-default-font-color',
    { 'font-bold': active },
  ]
}

// effects
/**
 * Reveal the current route's parent however it was reached. Keyed on the
 * parent, so moving between its children leaves a collapse in place.
 */
watch(
  currentItem,
  (item) => {
    if (item?.children) expandedItems.add(item.routeName)
  },
  { immediate: true },
)

// lifecycle
onMounted(() => {
  // The Users warning reads the organizations' admins, which only user managers can.
  if (auth.can('manageUsers')) userRolesStore.fetchAll()
})
</script>

<template>
  <aside
    :class="[
      'relative flex h-screen shrink-0 flex-col border-r-2 border-border bg-background transition-width dark:border-gray-700 dark:bg-navy',
      sidebarCollapsed ? 'w-16' : 'w-64',
    ]"
  >
    <RouterLink
      :to="{ name: 'home' }"
      class="flex h-[62px] shrink-0 items-center gap-x-3 overflow-hidden border-b-2 border-border px-4 dark:border-gray-700"
    >
      <img
        :src="
          darkMode.isDark
            ? '/logos/galvanick-yellow.svg'
            : '/logos/galvanick-blue.svg'
        "
        class="shrink-0"
        width="28"
        height="auto"
        alt="Galvanick Logo"
      />
      <span
        v-if="!sidebarCollapsed"
        class="mt-1 truncate font-eurostile text-xl font-extrabold leading-none text-foreground"
      >
        GALVANICK
      </span>
    </RouterLink>

    <button
      class="absolute inset-y-0 -right-px z-0 w-1 cursor-pointer hover:bg-muted-foreground/30 dark:hover:bg-muted-foreground/60"
      aria-label="Toggle sidebar"
      @click="sidebarCollapsed = !sidebarCollapsed"
    />

    <button
      class="absolute -right-3 top-5 z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 border-border bg-background dark:border-gray-700 dark:bg-navy"
      @click="sidebarCollapsed = !sidebarCollapsed"
    >
      <ChevronLeftIcon v-if="!sidebarCollapsed" :size="14" />
      <ChevronRightIcon v-else :size="14" />
    </button>

    <nav
      :class="[
        'flex flex-1 flex-col overflow-y-auto py-2',
        sidebarCollapsed ? 'gap-y-1' : 'gap-y-6',
      ]"
    >
      <template v-for="(group, idx) in groups" :key="group.label">
        <div
          v-if="sidebarCollapsed && idx > 0"
          class="mx-3 my-2 border-t border-muted-foreground/40"
        />
        <div>
          <div
            v-if="!sidebarCollapsed"
            class="flex items-center gap-x-2 px-4 pb-1"
          >
            <span
              class="whitespace-nowrap text-xs font-semibold uppercase text-muted-foreground"
            >
              {{ group.label }}
            </span>
            <div class="flex-1 border-t border-border dark:border-gray-700" />
          </div>
          <div class="flex flex-col gap-y-1">
            <div v-for="item in group.items" :key="item.routeName">
              <div
                :class="[
                  'flex items-center hover:bg-accent dark:hover:bg-muted-foreground/20',
                  {
                    'bg-accent dark:bg-muted-foreground/20': isItemActive(item),
                  },
                ]"
              >
                <RouterLink
                  v-tooltip="{
                    content: item.title,
                    placement: 'right',
                    disabled: !sidebarCollapsed,
                  }"
                  :to="{ name: item.routeName }"
                  :class="itemLinkClasses(item)"
                  @click="item.children && onParentClick(item)"
                >
                  <span class="relative inline-flex shrink-0 items-center">
                    <component
                      :is="item.icon"
                      :size="18"
                      :stroke-width="isItemActive(item) ? 2.75 : 2"
                    />
                    <Badge
                      v-if="item.badge && sidebarCollapsed"
                      :class="[
                        severityBg[item.badge.severity],
                        item.badgeText.collapsed,
                        'absolute -right-3 -top-1 flex h-4 min-w-4 items-center justify-center border-transparent px-0.5 pb-0 font-bold text-background',
                      ]"
                    >
                      {{ item.badge.label }}
                    </Badge>
                    <TriangleAlertIcon
                      v-if="item.warning && sidebarCollapsed"
                      :size="14"
                      class="absolute -right-3 -top-1 text-warn"
                    />
                  </span>
                  <!-- Trimmed to cap height so it centers like the icon; py-1 keeps descenders inside truncate's clip. -->
                  <span
                    v-if="!sidebarCollapsed"
                    class="flex-1 truncate py-1 leading-6 [text-box:trim-both_cap_alphabetic]"
                    >{{ item.title }}</span
                  >
                  <Badge
                    v-if="item.badge && !sidebarCollapsed"
                    :class="[
                      severityBg[item.badge.severity],
                      item.badgeText.expanded,
                      'flex h-5 w-5 items-center justify-center border-transparent px-0 pb-0 font-bold text-background',
                    ]"
                  >
                    {{ item.badge.label }}
                  </Badge>
                  <TriangleAlertIcon
                    v-if="item.warning && !sidebarCollapsed"
                    :size="18"
                    class="shrink-0 text-warn"
                  />
                </RouterLink>
                <button
                  v-if="item.children && !sidebarCollapsed"
                  class="ml-2 mr-4 flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted-foreground/20 hover:text-default-font-color dark:hover:bg-muted-foreground/35"
                  @click="toggleExpanded(item.routeName)"
                >
                  <ChevronDownIcon
                    :size="14"
                    :class="[
                      'transition-transform',
                      { '-rotate-90': !expandedItems.has(item.routeName) },
                    ]"
                  />
                </button>
              </div>
              <div
                v-if="
                  item.children &&
                  !sidebarCollapsed &&
                  expandedItems.has(item.routeName)
                "
                class="flex flex-col gap-y-0.5"
              >
                <!-- pt-2.5 pb-0.5: the label's box ends in its 4px underline slot, so the text centers only with more padding on top. -->
                <RouterLink
                  v-for="child in item.children"
                  :key="child.routeName"
                  :to="{ name: child.routeName }"
                  :class="[
                    'flex items-center gap-x-4 border-l-4 border-transparent pb-0.5 pl-4 pr-4 pt-2.5 text-sm',
                    route.name === child.routeName
                      ? 'font-medium text-default-font-color'
                      : 'text-muted-foreground hover:text-default-font-color',
                  ]"
                >
                  <span class="inline-block h-[18px] w-[18px] shrink-0" />
                  <!-- Every child reserves the active underline so selecting one doesn't shift its text. -->
                  <span
                    :class="[
                      'truncate border-b-2 pb-0.5 leading-none',
                      route.name === child.routeName
                        ? 'border-logo-font-color'
                        : 'border-transparent',
                    ]"
                    >{{ child.title }}</span
                  >
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
      </template>
    </nav>
  </aside>
</template>
